import { TicketIcon, XIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { Timeline } from '@/components/common/timeline';
import { Drawer } from '@/components/feedback/drawer';
import { formatDayMonthYearTime } from '@/lib/date';

import { activityChip, CLAIM_STATUS_CHIP, WARRANTY_COPY, WARRANTY_REASONS } from '../constants';
import { useWarrantyClaim } from '../hooks';
import type { WarrantyClaimDetail, WarrantyClaimStatus } from '../schemas';

import { ImageLightbox, type LightboxImage } from './image-lightbox';
import styles from './warranty-preview-drawer.module.scss';

export type WarrantyPreviewDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** The claim being previewed. */
  claimId: string;
};

/**
 * The header cluster, rendered inside `<Drawer title>` — so `OverlayChrome`'s `<h2>` gives the
 * panel its accessible name ("Ticket Preview Approved") and the status badge is part of it.
 */
function HeaderTitle({ status }: { status?: WarrantyClaimStatus }) {
  const chip = status ? CLAIM_STATUS_CHIP[status] : undefined;
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerHeading}>{WARRANTY_COPY.PREVIEW_TITLE}</span>
      {chip ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
    </span>
  );
}

/**
 * The claim's evidence images, named once. The grid and the lightbox both read this, so a
 * thumbnail and the full-size image it opens can never carry different accessible names.
 */
function toLightboxImages(claim: WarrantyClaimDetail): LightboxImage[] {
  return claim.images.map((image, index) => ({
    id: image.id,
    url: image.url,
    alt: `Evidence image ${index + 1}`,
  }));
}

function ClaimBody({
  claim,
  onOpenImage,
}: {
  claim: WarrantyClaimDetail;
  onOpenImage: (index: number, opener: HTMLButtonElement) => void;
}) {
  const images = toLightboxImages(claim);

  return (
    <>
      <div className={styles.identityGroup}>
        <div className={styles.identity}>
          <span className={styles.name}>{claim.title}</span>
          <span className={styles.subtitle}>{claim.subtitle}</span>
        </div>

        {/* No visible "Device Images" heading: no frame draws one, and the AC's name for the
            region is a name, not copy. `role="presentation"` keeps antd <Timeline>'s entries the
            only `listitem`s in the panel, which is what the tests and a screen reader both want —
            these tiles are pictures, not a list of things. It is repeated on each `<li>` on
            purpose: presentational-role inheritance to required owned elements is spec'd but not
            implemented by every AT (nor by `dom-accessibility-api`, which the tests query
            through), so the list alone would leave seven `listitem`s in the panel.
            The tile is the whole button — fewer elements, and the keyboard gets the same target
            the mouse does. Its accessible name comes from the `<img alt>` it wraps, so one string
            names the thumbnail, the full-size image and the filmstrip tile alike. */}
        {images.length ? (
          <ul className={styles.thumbGrid} role="presentation">
            {images.map((image, index) => (
              <li key={image.id} className={styles.thumb} role="presentation">
                <button
                  type="button"
                  className={styles.thumbButton}
                  onClick={(event) => onOpenImage(index, event.currentTarget)}
                >
                  <img className={styles.thumbImg} src={image.url} alt={image.alt} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className={styles.reasonCard}>
        {/* The client enum map, never the wire token and never Figma's off-enum "Device Broken"
            filler (context.md #20) — one casing stays ours. */}
        <h3 className={styles.reasonHeading}>{WARRANTY_REASONS[claim.reason]}</h3>
        <p className={styles.description}>{claim.description}</p>
      </div>

      <div>
        <h3 className={styles.timelineHeading}>{WARRANTY_COPY.ACTIVITY_TIMELINE}</h3>
        <Timeline
          className={styles.timeline}
          items={claim.timeline.map((entry) => {
            const chip = activityChip(entry.status);
            return {
              // A uniform grey dot in all three frames — the status colour lives in the pill, not
              // the node. 'pending' is the shared component's grey (--border-default #CCCCCC),
              // which is Figma's Border/Default exactly.
              status: 'pending' as const,
              title: <Chip intent={chip.intent}>{chip.label}</Chip>,
              timestamp: formatDayMonthYearTime(entry.timestamp),
            };
          })}
        />
      </div>

      {/* Rejected only, below the timeline. No reason on the wire → no section, never an empty red
          box. Copy is the AC's, and here Figma agrees with it. */}
      {claim.status === 'rejected' && claim.rejectionReason ? (
        <div className={styles.rejection} role="alert">
          <span className={styles.rejectionHeading}>
            <XIcon size={16} aria-hidden />
            {WARRANTY_COPY.REQUEST_REJECTED}
          </span>
          <p className={styles.rejectionValue}>{claim.rejectionReason}</p>
        </div>
      ) : null}
    </>
  );
}

/**
 * One Warranty claim in full, as a **second** slide-over stacked over the claims list
 * (113581 §D/§E, ticket 03).
 *
 * Nothing here can be acted on: approving and rejecting live in the Admin app, and Approved and
 * Rejected are terminal. Closing pops back to the list, which never unmounted — which is what
 * preserves its scroll position and its already-loaded pages (grilling Q5).
 *
 * `stacked` renders this drawer's mask transparent so the page is dimmed once, not twice, while an
 * outside click still closes only this panel. See plan.md § A note on `mask={false}`.
 *
 * Ticket 04 wires each evidence thumbnail into a full-size `<ImageLightbox>` mounted alongside the
 * panel body — see that component for why it renders inline rather than through a portal.
 */
export function WarrantyPreviewDrawer({ open, onClose, claimId }: WarrantyPreviewDrawerProps) {
  const query = useWarrantyClaim(claimId, open);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  // AC: closing the lightbox returns focus to the thumbnail that opened it. This has to be an
  // effect rather than a call inside `onClose`: at that moment the thumbnail is still inside the
  // `inert` region below, and a real browser silently ignores `.focus()` on an inert element.
  // By the time the effect runs React has removed the attribute.
  useEffect(() => {
    if (lightboxIndex === null) openerRef.current?.focus();
  }, [lightboxIndex]);

  function body() {
    // The closed panel owns nothing — same reasoning as the listing panel's.
    if (!open) return null;

    if (query.isPending) {
      return (
        <div className={styles.skeleton} role="status" aria-busy="true" aria-label="Loading claim">
          <div className={`${styles.skeletonBlock} ${styles.skeletonIdentity}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonThumbs}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonCard}`} />
          <div className={`${styles.skeletonBlock} ${styles.skeletonTimeline}`} />
        </div>
      );
    }

    if (query.isError) {
      return (
        <div className={styles.state}>
          {/* The live region is the message alone: wrapping Retry in it would have a screen reader
              announce the control as part of the alert. */}
          <p className={styles.stateText} role="alert">
            {WARRANTY_COPY.CLAIM_LOAD_FAILED}
          </p>
          <Button variant="secondary" onClick={() => void query.refetch()}>
            {WARRANTY_COPY.RETRY}
          </Button>
        </div>
      );
    }

    return (
      <ClaimBody
        claim={query.data}
        onOpenImage={(index, opener) => {
          openerRef.current = opener;
          setLightboxIndex(index);
        }}
      />
    );
  }

  // A claim with zero images can never legitimately have the lightbox open, but `lightboxIndex`
  // is local state that outlives a refetch — if a background refetch ever served the same claim
  // with its images gone, `lightboxIndex` would stay set while there is nothing left to show. That
  // state produced a deadlock: `inert` on the panel, `closeOnEsc` off, and the lightbox itself
  // rendering nothing (its own `if (!current) return null` bails after the fact), leaving no way
  // back in. Deriving one boolean from the same condition the lightbox render below already checks
  // — and using it for `inert` and `closeOnEsc` too — means the three can never disagree.
  const lightboxOpen = lightboxIndex !== null && !!query.data?.images.length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      stacked
      title={<HeaderTitle status={query.data?.status} />}
      cancelText=""
      // Second layer only. What actually keeps one Esc from closing this panel as well as the
      // lightbox is the lightbox's own `stopPropagation` — antd's Esc is a **bubble-phase**
      // `window` listener (`@rc-component/portal`'s `useEscKeyDown`), so it sits above the
      // lightbox in the propagation path and can be pre-empted from there.
      //
      // This prop cannot carry that on its own, and an earlier revision that relied on it shipped
      // the bug: a browser runs a microtask checkpoint between two listeners on one bubble path,
      // so React flushes the lightbox's close and re-renders — flipping this back to `true`
      // before `window` is reached. happy-dom skips that checkpoint, which is why the panel-level
      // Esc tests stayed green. It stays for the case where focus is somehow outside the lightbox
      // and the keypress never reaches it. See plan.md § Layering.
      closeOnEsc={!lightboxOpen}
    >
      {/* `inert` makes good on the lightbox's `aria-modal`: the panel behind it leaves the tab
          order and the a11y tree, with no hand-rolled focus trap. React 19 takes the boolean. */}
      <div className={styles.body} inert={lightboxOpen}>
        {body()}
      </div>
      {lightboxOpen && query.data ? (
        <ImageLightbox
          images={toLightboxImages(query.data)}
          initialIndex={lightboxIndex ?? 0}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}
    </Drawer>
  );
}
